import { useMemo } from "react";

import { milliseconds } from "date-fns";
import { identity, orderBy } from "lodash";

import useGetProposal from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import { TradeProposalId } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposals";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import { SecurityId } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

function useSecuritiesByTradeProposal(tradeProposalId: TradeProposalId) {
  const auth = useAdvisorRequestAuth();
  const proposal = useGetProposal(tradeProposalId, auth, false, {
    refetchInterval: milliseconds({ seconds: 10 }),
  });

  const allTradeSecIds = useMemo((): SecurityId[] | null => {
    if (proposal.data === undefined) {
      return null;
    }

    // Order them as the server does not guarantee stable ordering of trades
    const secIds = orderBy(
      proposal.data.trades.map((trade) => trade.securityId),
      [identity],
      ["asc"]
    );

    return secIds;
  }, [proposal.data]);

  const tradeSecurityRequest = useSecuritiesBySecurityIds(allTradeSecIds, auth);

  return tradeSecurityRequest;
}

// undefined is loading, null is error, and boolean is success
export function useIsMutualFunds(
  tradeProposalId: TradeProposalId
): undefined | null | boolean {
  const securitiesResponse = useSecuritiesByTradeProposal(tradeProposalId);

  const isMutualFunds = useMemo(() => {
    if (securitiesResponse.isLoading === true) {
      return undefined;
    }

    if (securitiesResponse.data === undefined) {
      return null;
    }

    return securitiesResponse.data.some(
      (security) =>
        security.type === "MutualFund" || security.type === "CashLike"
    );
  }, [securitiesResponse.data, securitiesResponse.isLoading]);

  return isMutualFunds;
}

export default useSecuritiesByTradeProposal;
